import type { V2 } from '../core/math';

export interface PursuitInfo {
  /** A police unit currently has line of sight to the player. */
  seen: boolean;
  /** Seconds since the player was last seen (0 while seen or when not wanted). */
  unseen: number;
  /** Where the cops last saw the player. */
  lastSeen: V2 | null;
  /** Estimated search radius (m). */
  radius: number;
}

export const SEARCH_MIN_RADIUS = 32;
export const SEARCH_MAX_RADIUS = 150;
export const SEARCH_GROWTH = 4;

/**
 * Fallback pursuit awareness for the HUD and minimap: derived from "can any police unit see the player",
 * used when the heat module doesn't publish its own `seen`/search-area. Pure and deterministic.
 */
export class PursuitTracker {
  readonly info: PursuitInfo = { seen: false, unseen: 0, lastSeen: null, radius: SEARCH_MIN_RADIUS };

  update(dt: number, heat: number, seen: boolean, player: V2): PursuitInfo {
    const i = this.info;
    if (heat <= 0) {
      i.seen = false;
      i.unseen = 0;
      i.lastSeen = null;
      i.radius = SEARCH_MIN_RADIUS;
      return i;
    }
    if (seen) {
      i.seen = true;
      i.unseen = 0;
      i.lastSeen = { x: player.x, z: player.z };
      i.radius = SEARCH_MIN_RADIUS;
    } else {
      i.seen = false;
      i.unseen += dt;
      i.lastSeen ??= { x: player.x, z: player.z };
      i.radius = Math.min(SEARCH_MAX_RADIUS, SEARCH_MIN_RADIUS + i.unseen * SEARCH_GROWTH);
    }
    return i;
  }
}
